/**
 * Author: marco
 * Last updated: 2025-05-30
 */

import { InNotes } from '../nodes/InNotes/InNotes.node';
import { IExecuteFunctions, INodeExecutionData } from 'n8n-workflow';

describe('InNotes Node', () => {
	let node: InNotes;
	let mockExecuteFunctions: Partial<IExecuteFunctions>;

	beforeEach(() => {
		node = new InNotes();
		mockExecuteFunctions = {
			getInputData: jest.fn().mockReturnValue([{ json: {} }] as INodeExecutionData[]),
			getNodeParameter: jest.fn(),
			getCredentials: jest.fn().mockResolvedValue({
				username: 'test@example.com',
				password: 'testpassword',
				baseUrl: 'https://test.innotes.com',
			}),
			helpers: {
				httpRequestWithAuthentication: jest.fn(),
				httpRequest: jest.fn(),
				requestWithAuthenticationPaginated: jest.fn(),
				request: jest.fn(),
				requestWithAuthentication: jest.fn(),
				requestOAuth2: jest.fn(),
				requestOAuth1: jest.fn(),
			},
		} as any;
	});

	describe('Node Properties', () => {
		it('should have correct description', () => {
			expect(node.description.displayName).toBe('InNotes');
			expect(node.description.name).toBe('inNotes');
			expect(node.description.group).toEqual(['transform']);
			expect(node.description.version).toBe(1);
		});

		it('should have correct credentials', () => {
			expect(node.description.credentials).toEqual([
				{
					name: 'inNotesApi',
					required: true,
				},
			]);
		});

		it('should have correct inputs and outputs', () => {
			expect(node.description.inputs).toEqual(['main']);
			expect(node.description.outputs).toEqual(['main']);
		});
	});

	describe('Resource Operations', () => {
		it('should have contact resource with correct operations', () => {
			const contactResource = node.description.properties?.find(
				prop => prop.name === 'resource' && prop.type === 'options',
			);

			expect(contactResource).toBeDefined();
			const contactOption = (contactResource as any)?.options?.find(
				(option: any) => option.value === 'contact',
			);
			expect(contactOption).toBeDefined();
			expect(contactOption.name).toBe('Contact');
		});

		it('should have note resource with correct operations', () => {
			const noteResource = node.description.properties?.find(
				prop => prop.name === 'resource' && prop.type === 'options',
			);

			expect(noteResource).toBeDefined();
			const noteOption = (noteResource as any)?.options?.find(
				(option: any) => option.value === 'note',
			);
			expect(noteOption).toBeDefined();
			expect(noteOption.name).toBe('Note');
		});
	});

	describe('Execute Method', () => {
		it('should handle contact get operation', async () => {
			const mockHttpRequest = jest.fn().mockResolvedValue({
				data: {
					id: 1,
					name: 'Test Contact',
					email: 'test@example.com',
				},
			});

			mockExecuteFunctions.helpers!.httpRequestWithAuthentication = mockHttpRequest;
			mockExecuteFunctions.getNodeParameter = jest.fn()
				.mockReturnValueOnce('contact') // resource
				.mockReturnValueOnce('get') // operation
				.mockReturnValueOnce(1); // contactId

			const result = await node.execute.call(mockExecuteFunctions as IExecuteFunctions);

			expect(mockHttpRequest).toHaveBeenCalledWith(
				'inNotesApi',
				expect.objectContaining({
					method: 'GET',
					url: '/api/contacts/1',
				}),
			);

			expect(result).toEqual([
				[
					{
						json: {
							id: 1,
							name: 'Test Contact',
							email: 'test@example.com',
						},
					},
				],
			]);
		});

		it('should handle note create operation', async () => {
			const mockHttpRequest = jest.fn().mockResolvedValue({
				data: {
					id: 1,
					content: 'Test note content',
					contactId: 1,
				},
			});

			mockExecuteFunctions.helpers!.httpRequestWithAuthentication = mockHttpRequest;
			mockExecuteFunctions.getNodeParameter = jest.fn()
				.mockReturnValueOnce('note') // resource
				.mockReturnValueOnce('create') // operation
				.mockReturnValueOnce('Test note content') // content
				.mockReturnValueOnce(1); // contactId

			const result = await node.execute.call(mockExecuteFunctions as IExecuteFunctions);

			expect(mockHttpRequest).toHaveBeenCalledWith(
				'inNotesApi',
				expect.objectContaining({
					method: 'POST',
					url: '/api/note',
					body: {
						content: 'Test note content',
						contactId: 1,
					},
				}),
			);

			expect(result).toEqual([
				[
					{
						json: {
							id: 1,
							content: 'Test note content',
							contactId: 1,
						},
					},
				],
			]);
		});

		it('should throw error for unsupported resource', async () => {
			mockExecuteFunctions.getNodeParameter = jest.fn()
				.mockReturnValueOnce('unsupported'); // resource

			await expect(
				node.execute.call(mockExecuteFunctions as IExecuteFunctions),
			).rejects.toThrow('The resource "unsupported" is not known!');
		});
	});
});
